/// TypeScript mirror of the Swift JSON schema in
/// FlatoutATC/Models/Models.swift (struct LevelData, struct Airport,
/// struct Runway, struct Waypoint, struct PresetAircraftData).
///
/// Only the fields the web MVP touches are typed. Unknown fields parse
/// fine because we don't validate strictly — we just `JSON.parse` and
/// trust the iOS-authored JSON.

export interface GeoCoordinate {
  latitude: number
  longitude: number
}

/// A high-elevation polygon that aircraft must stay above. Used by
/// terrain-rich levels (Innsbruck, Aspen, etc.) — see
/// FlatoutATC/Models/Models.swift `struct TerrainObstacle`.
export interface TerrainObstacle {
  elevationInFeet: number
  /// Closed polygon (last point doesn't need to repeat the first).
  coordinates: GeoCoordinate[]
}

export interface CodablePoint {
  x: number
  y: number
}

export interface Runway {
  id1: string
  id2: string
  startCoordinate: GeoCoordinate
  endCoordinate: GeoCoordinate
  heading1: number
  heading2: number
}

export interface Airport {
  name: string
  centerCoordinate: GeoCoordinate
  runways: Runway[]
  magneticVariation: number
  landingApproachDistance: number
  centerLineTolerance: number
  distanceRings?: number[]
  country?: string
  elevation?: number
  /// Standard Instrument Departures published for this airport.
  /// Each SID is bound to a specific runway end (`SID.runway`).
  sids?: SID[]
  /// Standard Terminal Arrival Routes published for this airport.
  stars?: STAR[]
  /// Optional motion for moving airports (e.g. aircraft carrier).
  /// `centerCoordinate` is the *initial* position; GameSession's
  /// per-tick airport-motion pass advances it along `motion` and
  /// translates the runway endpoints in lockstep so all geometry
  /// (touchdown detection, approach course, distance rings,
  /// runway slabs) tracks the moving deck.
  motion?: AirportMotion
  /// Cosmetic carrier flag — drives the hull polygon + angled
  /// deck visuals in CarrierLayer, the catapult-launch takeoff
  /// override, and the "no land basemap" rendering. Doesn't
  /// imply motion (you could in principle have a stationary
  /// carrier for a tutorial), but the level format expects them
  /// to be paired in practice.
  carrier?: boolean
  /// Helipads associated with the airport — rendered as circled
  /// "H" markers on the radar. Currently surface-only (helicopters
  /// are cleared to the airport's runway pool, not a specific pad);
  /// the per-pad goal targeting is on the backlog (P3).
  helipads?: Helipad[]
}

export interface Helipad {
  /// Short identifier like "H1" or "PAD-A". Drawn under the H glyph.
  id: string
  coordinate: GeoCoordinate
  /// Field elevation in feet. Falls back to the parent airport's
  /// elevation when omitted.
  elevation?: number
}

export interface AirportMotion {
  /// Course over ground in compass degrees.
  headingDeg: number
  /// Speed in knots. The session advances `centerCoordinate` by
  /// `(speedKts / 3600) × scaledDt` NM along `headingDeg` each
  /// tick.
  speedKts: number
}

/// `helipad` is a synthetic kind used only at runtime — when the
/// session loads, every `airport.helipads[]` entry is materialized
/// as a Waypoint (`name = pad.id`, `type = 'helipad'`) so the
/// player can pick it from the Direct-To menu. Helipads aren't
/// authored in `level.waypoints` directly.
export type WaypointKind = 'navaid' | 'fix' | 'helipad'

export interface Waypoint {
  name: string
  type: WaypointKind
  coordinate: GeoCoordinate
}

/// Mirrors Swift's enum AircraftType (`Models.swift:8-10`).
export type AircraftType =
  | 'light'
  | 'regionalJet'
  | 'heavy'
  | 'military'
  | 'helicopter'
  | 'fighter'

/// JSON shape of `enum AircraftGoal` after Swift's auto-synthesis.
/// e.g. `{ "land": { "airportName": "ACY" } }`. The web port handles
/// the air-only goal types: land, transition, takeoff, departure
/// (SID), arrival (STAR). The ground-control goals (parkGate /
/// parkTerminal / depart / departFromThreshold) are out of scope.
export interface AircraftGoal {
  land?: { airportName: string; runwayId?: string }
  transition?: { waypointName: string }
  takeoff?: { airportName: string; waypointName: string }
  /// Multi-leg SID departure. Aircraft starts on the runway, takes
  /// off via `commandTakeoff`, then auto-flies the SID's legs (with
  /// altitude/speed restrictions) until the last waypoint = exit fix.
  departure?: { airportName: string; sidName: string; runwayId: string }
  /// Multi-leg STAR arrival. Aircraft spawns on the radar edge aimed
  /// at the STAR's first waypoint, auto-flies the legs, and the last
  /// waypoint hands off to approach (auto-engages localizer-intercept).
  arrival?: { airportName: string; starName: string; runwayId?: string }
  /// Helicopter touchdown on a helipad attached to an airport. The
  /// player vectors the helo close to the pad; auto-engages at
  /// ~1.5 NM lateral, descends to pad elevation, touches down at
  /// 0.1 NM / pad-elevation + 10 ft. Mirrors iOS `.landHelipad`.
  landHelipad?: { airportName: string; helipadId: string }
  /// Aerial-survey / banner-tow goal — light aircraft is spawned at
  /// the radar edge aimed at `circleWaypointName`, auto-engages a
  /// hold on arrival, runs the hold for `holdDurationSec` of sim
  /// time, then auto-flies direct to `exitWaypointName`. Counts
  /// toward the level's handled denominator on exit but awards no
  /// score bonus — "decoration" traffic the player has to keep
  /// clear of. Mirrors iOS `.survey`.
  survey?: { circleWaypointName: string; exitWaypointName: string; holdDurationSec: number }
}

/// One leg of a SID or STAR. The waypoint is the "fly to" target;
/// optional altitude/speed restrictions are applied as the aircraft
/// engages this leg. Mirrors `struct ProcedureLeg` in iOS.
export interface ProcedureLeg {
  waypointName: string
  altitudeRestriction?: number | null
  speedRestriction?: number | null
}

/// Standard Instrument Departure — runway-specific, multi-leg.
export interface SID {
  name: string
  runway: string
  legs: ProcedureLeg[]
}

/// Standard Terminal Arrival Route — runway-agnostic, multi-leg.
export interface STAR {
  name: string
  legs: ProcedureLeg[]
}

/// Configures a batch of "random" aircraft to spawn at level start.
export interface InitialSpawnConfig {
  type: AircraftType
  count: number
  goal: AircraftGoal
}

/// Pool entry consulted by `randomAircraft` and `timed` scenarios.
/// Each entry is one possible goal an aircraft can be assigned at
/// spawn time. `allowedTypes` (when present and non-empty) restricts
/// the spawner to those types for this goal — e.g., a heavy is only
/// allowed for the long-runway airport's land goal.
export interface PossibleGoal {
  goal: AircraftGoal
  allowedTypes?: AircraftType[] | null
}

export interface PresetAircraftData {
  type: AircraftType
  goal: AircraftGoal
  flightNumber?: string
  initialPosition?: CodablePoint
  initialHeading?: number
  initialSpeedInKnots?: number
  initialAltitude?: number
}

/// JSON shape: `{ "presetAircraft": { "script": [...], "initialSpawn": [...] } }`.
/// MVP only consumes `presetAircraft`; `randomAircraft` and `timed`
/// scenarios share the same `initialSpawn` slot.
export interface LevelScenario {
  presetAircraft?: { script: PresetAircraftData[]; initialSpawn?: InitialSpawnConfig[] }
  randomAircraft?: { count: number; initialSpawn?: InitialSpawnConfig[] }
  timed?: { durationInSeconds: number; initialSpawn?: InitialSpawnConfig[] }
}

/// LocalizedString in Swift accepts either a plain string or a dict
/// keyed by language code. Both forms are valid here.
export type LocalizedString = string | { [lang: string]: string }

export function localized(s: LocalizedString | undefined): string {
  if (s == null) return ''
  if (typeof s === 'string') return s
  const lang = (navigator.language || 'en').slice(0, 2)
  return s[lang] ?? s.en ?? ''
}

/// Short user-facing description of a goal — shown in the command
/// panel and used by acknowledgement strings.
export function describeGoal(goal: AircraftGoal): string {
  if (goal.land) return `Land at ${goal.land.airportName}`
  if (goal.transition) return `Exit via ${goal.transition.waypointName}`
  if (goal.takeoff) return `Depart ${goal.takeoff.airportName} → ${goal.takeoff.waypointName}`
  if (goal.departure)
    return `Depart ${goal.departure.airportName} via ${goal.departure.sidName}`
  if (goal.arrival)
    return `Arrive ${goal.arrival.airportName} via ${goal.arrival.starName}`
  return 'Unknown goal'
}

/// Tight glyph + target string for the radar data tag — fits on one
/// line beside altitude/speed. Mirrors how iOS abbreviates the goal
/// in flight strips: ↓ for arrivals, → for transitions, ↑ for departures.
export function compactGoal(goal: AircraftGoal): string {
  if (goal.land) return `↓${goal.land.airportName}`
  if (goal.transition) return `→${goal.transition.waypointName}`
  if (goal.takeoff) return `↑${goal.takeoff.waypointName}`
  if (goal.departure) return `↑${goal.departure.sidName}`
  if (goal.arrival) return `↓${goal.arrival.starName}`
  return ''
}

export interface DifficultySettings {
  rateOfAircraftPerHour: number
  emergencyChance: number
  scoreMultiplier: number
  minimumSuccessPercentage: number
  /// Per-spawn-roll probability that a thunderstorm warning is
  /// scheduled. iOS uses 0.0–0.5 across difficulty tiers. Absent or
  /// 0 ⟹ no weather on this level. The roll fires every 180–300 s.
  thunderstormChance?: number
  /// Per-spawn probability (0–1) that an aircraft is AI-controlled — it flies
  /// itself to its goal (auto-land / auto-exit) via `AIController`. Absent or
  /// 0 ⟹ every aircraft is player-controlled.
  aiControlledPercentage?: number
}

/// Pre-computed level-wide goal mix. Built at session construction
/// from the script + initialSpawn + (future) randomAircraft pool, so
/// the brief and HUD can show the player what success looks like
/// before — and during — play.
export interface LevelGoalsSummary {
  landings: number
  transitions: number
  departures: number
  /// landings + transitions + departures.
  total: number
  /// 0–1, from the active difficulty's minimumSuccessPercentage.
  /// 0 ⟹ tutorial-style (any successful goal passes).
  requiredPct: number
  /// Number of goals that must be completed for the level to pass.
  /// `Math.ceil(total * requiredPct)`, or 1 when requiredPct is 0.
  targetCompleted: number
}

/// Live ADS-B traffic config. Present only on Handoff levels: instead of the
/// scripted/pool spawner, the session pulls real aircraft from a feed proxy,
/// classifies them, and injects them at their true positions.
/// A selectable shift position: a set of airports the player works. Arrivals to
/// airports NOT in the chosen position fly themselves (AI). Mirrors the iOS
/// LiveRegion.Position (Kennedy / LaGuardia / Newark+Teterboro / Combined).
export interface LiveShiftPosition {
  /// Stable id, persisted as the player's pick (e.g. 'kennedy', 'combined').
  id: string
  /// Button label shown in the pre-shift picker.
  label: string
  /// ICAOs the player works at this position.
  airports: string[]
}

export interface LiveTrafficConfig {
  /// Classifier region id (currently only 'n90' = New York TRACON).
  region: string
  /// Same-origin proxy URL (Hosting rewrite → Cloud Function → adsb.lol).
  endpoint: string
  /// Poll cadence in seconds (≥5; the feed adds jitter).
  pollSeconds?: number
  /// ICAOs that are real airports in this level — arrivals to these get a
  /// landing goal; everything else passes through (hand it off at a boundary).
  playableAirports: string[]
  /// Pre-shift position options. Present ⟹ the level opens on a position
  /// picker and holds the feed until the player starts the shift; arrivals to
  /// airports outside the chosen position fly themselves (AI).
  positions?: LiveShiftPosition[]
  /// Aircraft-worked goal presets offered in the picker (default [10, 20, 40]).
  shiftGoalPresets?: number[]
}

export interface LevelData {
  id: string
  levelName: LocalizedString
  description: LocalizedString
  /// Live ADS-B traffic source (Handoff levels only).
  liveTraffic?: LiveTrafficConfig
  /// True when the level is playable without any purchase. False
  /// (or absent) ⟹ the level is gated behind the single full-game
  /// unlock IAP. iOS's per-level `packId` field is ignored on web —
  /// the web port collapses all paid levels into a single $4.99
  /// FULL_UNLOCK product.
  isFree?: boolean
  worldSizeInNM: number
  airports: Airport[]
  waypoints: Waypoint[]
  scenario: LevelScenario
  /// Goal pool used by `randomAircraft` and `timed` scenarios. Each
  /// pool entry is one of the goal types the level wants spawned;
  /// `allowedTypes` narrows which aircraft types can carry that goal.
  /// Unused for pure `presetAircraft.script` levels.
  possibleGoals?: PossibleGoal[]
  mapFileName: string
  initialFocusAirportName?: string
  riverFileName?: string | null
  buildingFileName?: string | null
  contourFileName?: string | null
  /// Road / town overlay GeoJSON. Polygons render as faint
  /// translucent fills (towns, urban blobs); LineStrings render as
  /// thin strokes (roads, rails). Visual context only — no
  /// gameplay effect.
  overlayFileName?: string | null
  fixedWindDirection?: number | null
  initialZoomLevel?: number
  levelBrief?: LocalizedString
  difficultySettings?: Record<string, DifficultySettings>
  /// Optional terrain polygons for levels with mountainous geometry.
  /// Aircraft below the polygon's elevation while inside its footprint
  /// crash into terrain (see CollisionDetector).
  terrainObstacles?: TerrainObstacle[]
  /// Optional minimum altitude floor for spawned aircraft — used by
  /// terrain-rich levels to keep arrivals above the highest peaks.
  minimumSpawnAltitude?: number
}
